import React, { useState } from "react";
import { Button } from "./ui/button";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  MODES, BOX_MODES, DISTANCES, VEHICLE_TYPES, LANES, RESULTS, TRACK_CONDITIONS,
} from "../constants/options";
import { CloudSun, Loader2, Save, Lock } from "lucide-react";

function Seg({ label, options, value, onChange, testid }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-mt-mutedfg">{label}</label>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            data-testid={`${testid}-${o.value}`}
            onClick={() => onChange(o.value)}
            className={`rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
              value === o.value
                ? "border-mt-primary bg-mt-primary text-white"
                : "border-mt-border bg-mt-card text-mt-text hover:border-mt-primary"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Fld({ label, name, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-mt-mutedfg">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        placeholder={placeholder}
        data-testid={testid || `field-${name}`}
        onChange={(e) => onChange(name, e.target.value)}
        className="w-full rounded-lg border border-mt-border bg-mt-card px-3 py-2 font-mono text-sm outline-none focus:border-mt-primary focus:ring-1 focus:ring-mt-primary"
      />
    </div>
  );
}

export function RunForm({ initial, onSubmit, submitLabel = "Save Run", disabled = false }) {
  const [run, setRun] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [weatherLoading, setWeatherLoading] = useState(false);

  const set = (name, value) => setRun((r) => ({ ...r, [name]: value }));
  const merge = (obj) => setRun((r) => ({ ...r, ...obj }));

  const is8 = run.distance === "1/8";

  const fetchWeather = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    setWeatherLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await api.get(`/weather`, { params: { lat: pos.coords.latitude, lon: pos.coords.longitude } });
          merge({ airTemp: data.airTemp, humidity: data.humidity, altitude: data.altitude });
          toast.success("Weather filled from your location");
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail));
        } finally {
          setWeatherLoading(false);
        }
      },
      () => {
        setWeatherLoading(false);
        toast.error("Location permission denied");
      }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (disabled) return;
    if (!run.track?.trim()) return toast.error("Track name is required");
    setSaving(true);
    try {
      await onSubmit(run);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="run-form">
      {/* Quick action: weather auto-fill */}
      <button type="button" onClick={fetchWeather} disabled={weatherLoading || disabled} data-testid="weather-button"
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-mt-primary bg-mt-primary/5 py-3 text-mt-primary transition-colors hover:bg-mt-primary/10 disabled:opacity-60">
        {weatherLoading ? <Loader2 size={18} className="animate-spin" /> : <CloudSun size={18} />}
        <span className="text-sm font-bold">Auto-fill Weather from my location</span>
      </button>

      {/* Setup */}
      <Section title="Setup">
        <Seg label="Mode" options={MODES} value={run.mode} onChange={(v) => set("mode", v)} testid="mode" />
        <Seg label="Box" options={BOX_MODES} value={run.boxMode} onChange={(v) => set("boxMode", v)} testid="box" />
        <Seg label="Distance" options={DISTANCES} value={run.distance} onChange={(v) => set("distance", v)} testid="distance" />
        <Seg label="Vehicle" options={VEHICLE_TYPES} value={run.vehicleType} onChange={(v) => set("vehicleType", v)} testid="vehicle" />
      </Section>

      {/* Event */}
      <Section title="Event">
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Track" name="track" value={run.track} onChange={set} placeholder="Empire Dragway" />
          <Fld label="Date" name="date" type="date" value={run.date} onChange={set} />
          <Fld label="Class" name="raceClass" value={run.raceClass} onChange={set} placeholder="Stock" />
          <Fld label="Dial-In" name="dialIn" value={run.dialIn} onChange={set} placeholder="9.90" />
        </div>
        <Seg label="Lane" options={LANES} value={run.lane} onChange={(v) => set("lane", v)} testid="lane" />
      </Section>

      {/* Timing */}
      <Section title="My Timing">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Fld label="Reaction" name="reactionTime" value={run.reactionTime} onChange={set} placeholder=".025" />
          <Fld label="60 ft" name="sixtyFoot" value={run.sixtyFoot} onChange={set} placeholder="1.54" />
          <Fld label="330 ft" name="threeThirtyFoot" value={run.threeThirtyFoot} onChange={set} placeholder="4.08" />
          <Fld label={is8 ? "1/8 ET" : "660 ft (1/8 ET)"} name="sixSixtyFoot" value={run.sixSixtyFoot} onChange={set} placeholder="6.23" />
          {!is8 && <Fld label="1000 ft" name="thousandFoot" value={run.thousandFoot} onChange={set} placeholder="8.90" />}
          <Fld label={is8 ? "1/8 ET (final)" : "1/4 ET"} name="et" value={run.et} onChange={set} placeholder="9.75" />
          <Fld label="Trap MPH" name="speed" value={run.speed} onChange={set} placeholder="138.4" />
        </div>
      </Section>

      {/* Opponent */}
      <Section title="Opponent (optional)">
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Opp Reaction" name="opponentReactionTime" value={run.opponentReactionTime} onChange={set} />
          <Fld label="Opp ET" name="opponentET" value={run.opponentET} onChange={set} />
          <Fld label="Opp MPH" name="opponentSpeed" value={run.opponentSpeed} onChange={set} />
          <Fld label="Opp Dial-In" name="opponentDialIn" value={run.opponentDialIn} onChange={set} />
        </div>
        <Seg label="Result" options={RESULTS} value={run.result} onChange={(v) => set("result", v)} testid="result" />
      </Section>

      {/* Conditions */}
      <Section title="Conditions & Setup">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Fld label="Tire PSI Front" name="tirePressureFront" value={run.tirePressureFront} onChange={set} />
          <Fld label="Tire PSI Rear" name="tirePressureRear" value={run.tirePressureRear} onChange={set} />
          <Fld label="Engine Temp" name="engineTemp" value={run.engineTemp} onChange={set} />
          <Fld label="Air Temp °F" name="airTemp" value={run.airTemp} onChange={set} />
          <Fld label="Humidity %" name="humidity" value={run.humidity} onChange={set} />
          <Fld label="Altitude (ft)" name="altitude" value={run.altitude} onChange={set} />
        </div>
        <Seg label="Track Condition" options={TRACK_CONDITIONS} value={run.trackCondition} onChange={(v) => set("trackCondition", v)} testid="track-condition" />
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Points" name="points" value={run.points} onChange={set} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-mt-mutedfg">Notes</label>
          <textarea value={run.notes} onChange={(e) => set("notes", e.target.value)} data-testid="field-notes"
            className="h-24 w-full resize-none rounded-lg border border-mt-border bg-mt-card p-3 text-sm outline-none focus:border-mt-primary focus:ring-1 focus:ring-mt-primary"
            placeholder="How did the run feel? Any changes made?" />
        </div>
      </Section>

      {disabled ? (
        <div className="flex items-center justify-center gap-2 rounded-xl border border-mt-border bg-mt-secondary/60 py-4 text-sm font-bold text-mt-mutedfg" data-testid="run-form-locked">
          <Lock size={16} /> Trial ended — upgrade to edit runs
        </div>
      ) : (
        <Button type="submit" disabled={saving} data-testid="submit-run-button"
          className="w-full gap-2 bg-mt-primary py-6 text-base font-bold text-white hover:bg-mt-primary/90">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} {submitLabel}
        </Button>
      )}
    </form>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-4 rounded-xl border border-mt-border bg-mt-card p-4 shadow-sm">
      <h3 className="font-head text-sm font-bold uppercase tracking-[0.2em] text-mt-primary">{title}</h3>
      {children}
    </div>
  );
}
